/// <reference types="./comment.d.mts" />
import * as $comment from "../../../common/data/comment.mjs";
import * as $bool from "../../../gleam_stdlib/gleam/bool.mjs";
import * as $dict from "../../../gleam_stdlib/gleam/dict.mjs";
import * as $list from "../../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../../../gleam_stdlib/gleam/pair.mjs";
import * as $result from "../../../gleam_stdlib/gleam/result.mjs";
import * as $timestamp from "../../../gleam_time/gleam/time/timestamp.mjs";
import * as $effect from "../../../lustre/lustre/effect.mjs";
import * as $model from "../../data/model.mjs";
import * as $state from "../../data/model/state.mjs";
import { State } from "../../data/model/state.mjs";
import * as $msg from "../../data/msg.mjs";
import * as $proposals from "../../data/proposals.mjs";
import * as $comments from "../../frontend/effects/comments.mjs";
import * as $dom from "../../frontend/effects/dom.mjs";
import { Ok, toList, prepend as listPrepend } from "../../gleam.mjs";

function same_react(r1, r2) {
  return (r1.value === r2.value) && (r1.owner === r2.owner);
}

function local_toggle_react(comment, react) {
  let _block;
  let $ = $list.find(
    comment.reacts,
    (_capture) => { return same_react(_capture, react); },
  );
  if ($ instanceof Ok) {
    let react$1 = $[0];
    let _pipe = comment.reacts;
    _block = $list.filter(_pipe, (r) => { return !same_react(r, react$1); });
  } else {
    let _pipe = comment.reacts;
    _block = $list.append(_pipe, toList([react]));
  }
  let reacts = _block;
  let _record = comment;
  return new $comment.Comment(
    _record.id,
    _record.owner,
    _record.value,
    _record.org_id,
    _record.target_id,
    _record.is_resolved,
    _record.is_deleted,
    reacts,
    _record.history,
    _record.mentions,
    _record.updated_at,
    _record.created_at,
  );
}

function insert_comments(existing, to_add) {
  let _pipe = existing;
  let _pipe$1 = $dict.from_list(_pipe);
  let _pipe$2 = $dict.combine(
    _pipe$1,
    (() => {
      let _pipe$2 = to_add;
      return $list.group(_pipe$2, (c) => { return c.target_id; });
    })(),
    $list.append,
  );
  return $dict.to_list(_pipe$2);
}

function upsert_comments(existing, replacing) {
  let _pipe = existing;
  let _pipe$1 = $dict.from_list(_pipe);
  let _pipe$2 = $dict.combine(
    _pipe$1,
    (() => {
      let _pipe$2 = replacing;
      return $list.group(_pipe$2, (c) => { return c.target_id; });
    })(),
    (existing, replacing) => {
      let _pipe$2 = $dict.combine(
        (() => {
          let _pipe$2 = existing;
          return $list.group(_pipe$2, (c) => { return c.id; });
        })(),
        (() => {
          let _pipe$2 = replacing;
          return $list.group(_pipe$2, (c) => { return c.id; });
        })(),
        (_, new$) => { return new$; },
      );
      let _pipe$3 = $dict.values(_pipe$2);
      return $list.flatten(_pipe$3);
    },
  );
  return $dict.to_list(_pipe$2);
}

function remove_comments(existing, to_delete) {
  let _pipe = existing;
  let _pipe$1 = $list.flat_map(_pipe, $pair.second);
  let _pipe$2 = $list.filter(
    _pipe$1,
    (c) => {
      let _pipe$2 = $list.contains(to_delete, c.id);
      return $bool.negate(_pipe$2);
    },
  );
  let _pipe$3 = $list.group(_pipe$2, (c) => { return c.target_id; });
  return $dict.to_list(_pipe$3);
}

export function update(state, msg) {
  if (msg instanceof $msg.ApiReturnedComments) {
    let comments = msg[0];
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          (() => {
            let _pipe = comments;
            let _pipe$1 = $list.group(_pipe, (c) => { return c.target_id; });
            return $dict.to_list(_pipe$1);
          })(),
          _record.comments_draft_mentions,
          _record.comments_in_edition,
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          _record.draft_comments,
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else if (msg instanceof $msg.ApiCreatedComments) {
    let comments = msg[0];
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          insert_comments(state.comments, comments),
          _record.comments_draft_mentions,
          $dict.drop(
            state.comments_in_edition,
            (() => {
              let _pipe = comments;
              return $list.map(_pipe, (c) => { return c.id; });
            })(),
          ),
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          $dict.drop(
            state.draft_comments,
            (() => {
              let _pipe = comments;
              return $list.map(_pipe, (c) => { return c.target_id; });
            })(),
          ),
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else if (msg instanceof $msg.ApiUpdatedComments) {
    let comments = msg[0];
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          upsert_comments(state.comments, comments),
          _record.comments_draft_mentions,
          $dict.drop(
            state.comments_in_edition,
            (() => {
              let _pipe = comments;
              return $list.map(_pipe, (c) => { return c.id; });
            })(),
          ),
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          $dict.drop(
            state.draft_comments,
            (() => {
              let _pipe = comments;
              return $list.map(_pipe, (c) => { return c.target_id; });
            })(),
          ),
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else if (msg instanceof $msg.ApiDeletedComments) {
    let comments = msg[0];
    let _block;
    let _pipe = comments;
    _block = $list.map(_pipe, (c) => { return c.id; });
    let ids = _block;
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          remove_comments(state.comments, ids),
          _record.comments_draft_mentions,
          $dict.drop(state.comments_in_edition, ids),
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          _record.draft_comments,
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else if (msg instanceof $msg.UserArchivedComment) {
    let comment_id = msg[0];
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          remove_comments(state.comments, toList([comment_id])),
          _record.comments_draft_mentions,
          $dict.drop(state.comments_in_edition, toList([comment_id])),
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          _record.draft_comments,
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $comments.archive_comment(comment_id),
    ];
  } else if (msg instanceof $msg.UserResolvedComment) {
    let comment = msg[0];
    return [
      (() => {
        let _record = state;
        return new State(
          _record.window_dimensions,
          _record.access_token,
          _record.analytics,
          _record.client,
          _record.collapsed_navbar,
          upsert_comments(
            state.comments,
            toList([
              (() => {
                let _record$1 = comment;
                return new $comment.Comment(
                  _record$1.id,
                  _record$1.owner,
                  _record$1.value,
                  _record$1.org_id,
                  _record$1.target_id,
                  !comment.is_resolved,
                  _record$1.is_deleted,
                  _record$1.reacts,
                  _record$1.history,
                  _record$1.mentions,
                  _record$1.updated_at,
                  _record$1.created_at,
                );
              })(),
            ]),
          ),
          _record.comments_draft_mentions,
          _record.comments_in_edition,
          _record.connectors,
          _record.content_library,
          _record.copilot,
          _record.custom_rewording_input,
          _record.draft_comments,
          _record.dropdown_unsubscriber,
          _record.feed_opened,
          _record.language,
          _record.loading,
          _record.modal,
          _record.modal_unsubscriber,
          _record.notifications,
          _record.notifications_unread,
          _record.opened_projects,
          _record.organization,
          _record.permissions,
          _record.projects,
          _record.proposal_builder,
          _record.proposals,
          _record.qualification_matrix,
          _record.route,
          _record.running_requests,
          _record.translations,
          _record.user,
          _record.sources_tooltip,
          _record.sources_tooltip_unsubscriber,
          _record.maintenance,
        );
      })(),
      $comments.resolve_comment(comment.id),
    ];
  } else if (msg instanceof $msg.UserToggledReact) {
    let comment = msg.comment;
    let react = msg.react;
    let toggle_react = local_toggle_react(comment, react);
    let comments = upsert_comments(state.comments, toList([toggle_react]));
    let _pipe = state.proposals;
    let _pipe$1 = $proposals.close_emoji_comment_picker(_pipe);
    let _pipe$2 = ((_capture) => {
      return $state.set_proposals(
        (() => {
          let _record = state;
          return new State(
            _record.window_dimensions,
            _record.access_token,
            _record.analytics,
            _record.client,
            _record.collapsed_navbar,
            comments,
            _record.comments_draft_mentions,
            _record.comments_in_edition,
            _record.connectors,
            _record.content_library,
            _record.copilot,
            _record.custom_rewording_input,
            _record.draft_comments,
            _record.dropdown_unsubscriber,
            _record.feed_opened,
            _record.language,
            _record.loading,
            _record.modal,
            _record.modal_unsubscriber,
            _record.notifications,
            _record.notifications_unread,
            _record.opened_projects,
            _record.organization,
            _record.permissions,
            _record.projects,
            _record.proposal_builder,
            _record.proposals,
            _record.qualification_matrix,
            _record.route,
            _record.running_requests,
            _record.translations,
            _record.user,
            _record.sources_tooltip,
            _record.sources_tooltip_unsubscriber,
            _record.maintenance,
          );
        })(),
        _capture,
      );
    })(_pipe$1);
    return $pair.new$(
      _pipe$2,
      $comments.toggle_react_comment(comment.id, react),
    );
  } else if (msg instanceof $msg.UserToggledCommentMore) {
    let comment_id = msg.comment_id;
    let $ = state.proposals.popup_more_comment_id;
    if ($ instanceof Some) {
      let cid = $[0];
      if (cid === comment_id) {
        let _block;
        let _record = state.proposals;
        _block = new $proposals.Proposals(
          _record.all,
          _record.filters,
          _record.filters_opened,
          _record.selected_questions,
          _record.add_opened,
          _record.more_question_opened,
          _record.more_proposal_opened,
          _record.more_unsubscriber,
          _record.block_page_size,
          _record.collaborators_opened,
          _record.change_owner_popup_opened,
          _record.change_status_popup_opened,
          _record.comment_displayed_opened,
          _record.emoji_comment_picker_id,
          _record.title_opened,
          _record.focus_comment,
          new None(),
          _record.elements,
          _record.improvements_conversations,
          _record.conversations_input,
        );
        let _pipe = _block;
        let _pipe$1 = ((_capture) => {
          return $state.set_proposals(state, _capture);
        })(_pipe);
        return $pair.new$(_pipe$1, $model.unsubscribe_more_proposal(state));
      } else {
        let popup_more_comment_id = new Some(comment_id);
        let _block;
        let _record = state.proposals;
        _block = new $proposals.Proposals(
          _record.all,
          _record.filters,
          _record.filters_opened,
          _record.selected_questions,
          _record.add_opened,
          _record.more_question_opened,
          _record.more_proposal_opened,
          _record.more_unsubscriber,
          _record.block_page_size,
          _record.collaborators_opened,
          _record.change_owner_popup_opened,
          _record.change_status_popup_opened,
          _record.comment_displayed_opened,
          _record.emoji_comment_picker_id,
          _record.title_opened,
          _record.focus_comment,
          popup_more_comment_id,
          _record.elements,
          _record.improvements_conversations,
          _record.conversations_input,
        );
        let _pipe = _block;
        let _pipe$1 = ((_capture) => {
          return $state.set_proposals(state, _capture);
        })(_pipe);
        return $pair.new$(_pipe$1, $model.unsubscribe_more_proposal(state));
      }
    } else {
      let popup_more_comment_id = new Some(comment_id);
      let _block;
      let _record = state.proposals;
      _block = new $proposals.Proposals(
        _record.all,
        _record.filters,
        _record.filters_opened,
        _record.selected_questions,
        _record.add_opened,
        _record.more_question_opened,
        _record.more_proposal_opened,
        _record.more_unsubscriber,
        _record.block_page_size,
        _record.collaborators_opened,
        _record.change_owner_popup_opened,
        _record.change_status_popup_opened,
        _record.comment_displayed_opened,
        _record.emoji_comment_picker_id,
        _record.title_opened,
        _record.focus_comment,
        popup_more_comment_id,
        _record.elements,
        _record.improvements_conversations,
        _record.conversations_input,
      );
      let _pipe = _block;
      let _pipe$1 = ((_capture) => {
        return $state.set_proposals(state, _capture);
      })(_pipe);
      return $pair.new$(
        _pipe$1,
        (() => {
          let _pipe$2 = $dom.on_click(new $msg.UserClosedPopup("comment-more"));
          let _pipe$3 = $dom.to_cancel(
            _pipe$2,
            (var0) => { return new $msg.MenuReturnedUnsubscriber(var0); },
          );
          let _pipe$4 = $dom.once(_pipe$3);
          return $dom.subscribe(_pipe$4);
        })(),
      );
    }
  } else if (msg instanceof $msg.UserToggledCommentEdition) {
    let comment = msg.comment;
    let existing_comment = $dict.get(state.comments_in_edition, comment.id);
    let _block;
    if (existing_comment instanceof Ok) {
      _block = $dict.drop(state.comments_in_edition, toList([comment.id]));
    } else {
      _block = $dict.insert(
        state.comments_in_edition,
        comment.id,
        comment.value,
      );
    }
    let comments_in_edition = _block;
    let _pipe = state.proposals;
    let _pipe$1 = $proposals.close_popups(_pipe);
    let _pipe$2 = ((_capture) => {
      return $state.set_proposals(
        (() => {
          let _record = state;
          return new State(
            _record.window_dimensions,
            _record.access_token,
            _record.analytics,
            _record.client,
            _record.collapsed_navbar,
            _record.comments,
            _record.comments_draft_mentions,
            comments_in_edition,
            _record.connectors,
            _record.content_library,
            _record.copilot,
            _record.custom_rewording_input,
            _record.draft_comments,
            _record.dropdown_unsubscriber,
            _record.feed_opened,
            _record.language,
            _record.loading,
            _record.modal,
            _record.modal_unsubscriber,
            _record.notifications,
            _record.notifications_unread,
            _record.opened_projects,
            _record.organization,
            _record.permissions,
            _record.projects,
            _record.proposal_builder,
            _record.proposals,
            _record.qualification_matrix,
            _record.route,
            _record.running_requests,
            _record.translations,
            _record.user,
            _record.sources_tooltip,
            _record.sources_tooltip_unsubscriber,
            _record.maintenance,
          );
        })(),
        _capture,
      );
    })(_pipe$1);
    return $pair.new$(_pipe$2, $effect.none());
  } else if (msg instanceof $msg.UserEditedCommentEditionValue) {
    let comment_id = msg.comment_id;
    let value = msg.value;
    let _block;
    let _pipe = state.comments_in_edition;
    _block = $dict.insert(_pipe, comment_id, value);
    let comments_in_edition = _block;
    let _block$1;
    let _record = state;
    _block$1 = new State(
      _record.window_dimensions,
      _record.access_token,
      _record.analytics,
      _record.client,
      _record.collapsed_navbar,
      _record.comments,
      _record.comments_draft_mentions,
      comments_in_edition,
      _record.connectors,
      _record.content_library,
      _record.copilot,
      _record.custom_rewording_input,
      _record.draft_comments,
      _record.dropdown_unsubscriber,
      _record.feed_opened,
      _record.language,
      _record.loading,
      _record.modal,
      _record.modal_unsubscriber,
      _record.notifications,
      _record.notifications_unread,
      _record.opened_projects,
      _record.organization,
      _record.permissions,
      _record.projects,
      _record.proposal_builder,
      _record.proposals,
      _record.qualification_matrix,
      _record.route,
      _record.running_requests,
      _record.translations,
      _record.user,
      _record.sources_tooltip,
      _record.sources_tooltip_unsubscriber,
      _record.maintenance,
    );
    let _pipe$1 = _block$1;
    return $pair.new$(_pipe$1, $effect.none());
  } else if (msg instanceof $msg.UserSubmittedCommentEdition) {
    let comment = msg.comment;
    let $ = (() => {
      let _pipe = state.comments_in_edition;
      return $dict.get(_pipe, comment.id);
    })();
    if ($ instanceof Ok) {
      let value = $[0];
      let _pipe = new $comment.EditionPayload(value, comment.id);
      let _pipe$1 = $comments.update_comment(_pipe);
      return ((_capture) => { return $pair.new$(state, _capture); })(_pipe$1);
    } else {
      return [state, $effect.none()];
    }
  } else if (msg instanceof $msg.UserCancelCommentEdition) {
    let comment_id = msg.comment_id;
    let comments_in_edition = $dict.drop(
      state.comments_in_edition,
      toList([comment_id]),
    );
    let _block;
    let _record = state;
    _block = new State(
      _record.window_dimensions,
      _record.access_token,
      _record.analytics,
      _record.client,
      _record.collapsed_navbar,
      _record.comments,
      _record.comments_draft_mentions,
      comments_in_edition,
      _record.connectors,
      _record.content_library,
      _record.copilot,
      _record.custom_rewording_input,
      _record.draft_comments,
      _record.dropdown_unsubscriber,
      _record.feed_opened,
      _record.language,
      _record.loading,
      _record.modal,
      _record.modal_unsubscriber,
      _record.notifications,
      _record.notifications_unread,
      _record.opened_projects,
      _record.organization,
      _record.permissions,
      _record.projects,
      _record.proposal_builder,
      _record.proposals,
      _record.qualification_matrix,
      _record.route,
      _record.running_requests,
      _record.translations,
      _record.user,
      _record.sources_tooltip,
      _record.sources_tooltip_unsubscriber,
      _record.maintenance,
    );
    let data = _block;
    return [data, $effect.none()];
  } else if (msg instanceof $msg.UserEditedComment) {
    let comment = msg.comment;
    let target_id = msg.target_id;
    let _pipe = state;
    let _pipe$1 = $state.update_draft_comment(_pipe, comment, target_id);
    return $pair.new$(_pipe$1, $effect.none());
  } else if (msg instanceof $msg.UserSubmittedComment) {
    let target_id = msg[0];
    let _block;
    let _pipe = state.draft_comments;
    _block = $dict.get(_pipe, target_id);
    let value = _block;
    let _block$1;
    let _pipe$1 = state.user;
    _block$1 = $option.map(_pipe$1, (u) => { return u.sub; });
    let owner = _block$1;
    let _block$2;
    let _pipe$2 = state.user;
    _block$2 = $option.then$(_pipe$2, (u) => { return u.org_id; });
    let org_id = _block$2;
    let _block$3;
    let _pipe$3 = state.comments_draft_mentions;
    let _pipe$4 = $dict.get(_pipe$3, target_id);
    _block$3 = $result.unwrap(_pipe$4, toList([]));
    let mentions = _block$3;
    if (org_id instanceof Some) {
      if (owner instanceof Some) {
        if (value instanceof Ok) {
          let org_id$1 = org_id[0];
          let owner$1 = owner[0];
          let value$1 = value[0];
          let _pipe$5 = state;
          let _pipe$6 = $state.empty_draft_mentions(_pipe$5, target_id);
          return $pair.new$(
            _pipe$6,
            $comments.create_comment(
              new $comment.Comment(
                "",
                owner$1,
                value$1,
                org_id$1,
                target_id,
                false,
                false,
                toList([]),
                toList([]),
                mentions,
                $timestamp.system_time(),
                $timestamp.system_time(),
              ),
            ),
          );
        } else {
          return [state, $effect.none()];
        }
      } else {
        return [state, $effect.none()];
      }
    } else {
      return [state, $effect.none()];
    }
  } else if (msg instanceof $msg.UserCanceledComment) {
    let target_id = msg[0];
    let _pipe = state;
    let _pipe$1 = $state.update_draft_comment(_pipe, "", target_id);
    let _pipe$2 = $state.empty_draft_mentions(_pipe$1, target_id);
    return $pair.new$(_pipe$2, $effect.none());
  } else if (msg instanceof $msg.UserAddMentionToDraftComment) {
    let comment_id = msg.comment_id;
    let user_tagged = msg.user_tagged;
    let _pipe = state.comments_draft_mentions;
    let _pipe$1 = $dict.upsert(
      _pipe,
      comment_id,
      (existing) => {
        if (existing instanceof Some) {
          let existing$1 = existing[0];
          let $ = $list.find(existing$1, (e) => { return e === user_tagged; });
          if ($ instanceof Ok) {
            return existing$1;
          } else {
            return listPrepend(user_tagged, existing$1);
          }
        } else {
          return toList([user_tagged]);
        }
      },
    );
    let _pipe$2 = ((_capture) => {
      return $state.set_draft_mentions(state, _capture);
    })(_pipe$1);
    return $pair.new$(_pipe$2, $effect.none());
  } else if (msg instanceof $msg.UserRemoveMentionFromDraftComment) {
    let comment_id = msg.comment_id;
    let user_tagged = msg.user_tagged;
    let _pipe = state.comments_draft_mentions;
    let _pipe$1 = $dict.upsert(
      _pipe,
      comment_id,
      (existing) => {
        if (existing instanceof Some) {
          let existing$1 = existing[0];
          let _pipe$1 = existing$1;
          return $list.filter(_pipe$1, (e) => { return e !== user_tagged; });
        } else {
          return toList([]);
        }
      },
    );
    let _pipe$2 = ((_capture) => {
      return $state.set_draft_mentions(state, _capture);
    })(_pipe$1);
    return $pair.new$(_pipe$2, $effect.none());
  } else if (msg instanceof $msg.UserAddMentionToComment) {
    let comment = msg.comment;
    let user_tagged = msg.user_tagged;
    let _block;
    let _record = comment;
    _block = new $comment.Comment(
      _record.id,
      _record.owner,
      _record.value,
      _record.org_id,
      _record.target_id,
      _record.is_resolved,
      _record.is_deleted,
      _record.reacts,
      _record.history,
      listPrepend(user_tagged, comment.mentions),
      _record.updated_at,
      _record.created_at,
    );
    let comment$1 = _block;
    return [
      (() => {
        let _record$1 = state;
        return new State(
          _record$1.window_dimensions,
          _record$1.access_token,
          _record$1.analytics,
          _record$1.client,
          _record$1.collapsed_navbar,
          upsert_comments(state.comments, toList([comment$1])),
          _record$1.comments_draft_mentions,
          _record$1.comments_in_edition,
          _record$1.connectors,
          _record$1.content_library,
          _record$1.copilot,
          _record$1.custom_rewording_input,
          _record$1.draft_comments,
          _record$1.dropdown_unsubscriber,
          _record$1.feed_opened,
          _record$1.language,
          _record$1.loading,
          _record$1.modal,
          _record$1.modal_unsubscriber,
          _record$1.notifications,
          _record$1.notifications_unread,
          _record$1.opened_projects,
          _record$1.organization,
          _record$1.permissions,
          _record$1.projects,
          _record$1.proposal_builder,
          _record$1.proposals,
          _record$1.qualification_matrix,
          _record$1.route,
          _record$1.running_requests,
          _record$1.translations,
          _record$1.user,
          _record$1.sources_tooltip,
          _record$1.sources_tooltip_unsubscriber,
          _record$1.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else if (msg instanceof $msg.UserRemoveMentionFromComment) {
    let comment = msg.comment;
    let user_tagged = msg.user_tagged;
    let _block;
    let _record = comment;
    _block = new $comment.Comment(
      _record.id,
      _record.owner,
      _record.value,
      _record.org_id,
      _record.target_id,
      _record.is_resolved,
      _record.is_deleted,
      _record.reacts,
      _record.history,
      $list.filter(
        comment.mentions,
        (mention) => { return mention !== user_tagged; },
      ),
      _record.updated_at,
      _record.created_at,
    );
    let comment$1 = _block;
    return [
      (() => {
        let _record$1 = state;
        return new State(
          _record$1.window_dimensions,
          _record$1.access_token,
          _record$1.analytics,
          _record$1.client,
          _record$1.collapsed_navbar,
          upsert_comments(state.comments, toList([comment$1])),
          _record$1.comments_draft_mentions,
          _record$1.comments_in_edition,
          _record$1.connectors,
          _record$1.content_library,
          _record$1.copilot,
          _record$1.custom_rewording_input,
          _record$1.draft_comments,
          _record$1.dropdown_unsubscriber,
          _record$1.feed_opened,
          _record$1.language,
          _record$1.loading,
          _record$1.modal,
          _record$1.modal_unsubscriber,
          _record$1.notifications,
          _record$1.notifications_unread,
          _record$1.opened_projects,
          _record$1.organization,
          _record$1.permissions,
          _record$1.projects,
          _record$1.proposal_builder,
          _record$1.proposals,
          _record$1.qualification_matrix,
          _record$1.route,
          _record$1.running_requests,
          _record$1.translations,
          _record$1.user,
          _record$1.sources_tooltip,
          _record$1.sources_tooltip_unsubscriber,
          _record$1.maintenance,
        );
      })(),
      $effect.none(),
    ];
  } else {
    let comment_id = msg.id;
    let $ = state.proposals.emoji_comment_picker_id;
    if ($ instanceof Some) {
      let cid = $[0];
      if (cid === comment_id) {
        let _block;
        let _record = state.proposals;
        _block = new $proposals.Proposals(
          _record.all,
          _record.filters,
          _record.filters_opened,
          _record.selected_questions,
          _record.add_opened,
          _record.more_question_opened,
          _record.more_proposal_opened,
          _record.more_unsubscriber,
          _record.block_page_size,
          _record.collaborators_opened,
          _record.change_owner_popup_opened,
          _record.change_status_popup_opened,
          _record.comment_displayed_opened,
          new None(),
          _record.title_opened,
          _record.focus_comment,
          _record.popup_more_comment_id,
          _record.elements,
          _record.improvements_conversations,
          _record.conversations_input,
        );
        let _pipe = _block;
        let _pipe$1 = ((_capture) => {
          return $state.set_proposals(state, _capture);
        })(_pipe);
        return $pair.new$(_pipe$1, $model.unsubscribe_more_proposal(state));
      } else {
        let emoji_comment_picker_id = new Some(comment_id);
        let _block;
        let _record = state.proposals;
        _block = new $proposals.Proposals(
          _record.all,
          _record.filters,
          _record.filters_opened,
          _record.selected_questions,
          _record.add_opened,
          _record.more_question_opened,
          _record.more_proposal_opened,
          _record.more_unsubscriber,
          _record.block_page_size,
          _record.collaborators_opened,
          _record.change_owner_popup_opened,
          _record.change_status_popup_opened,
          _record.comment_displayed_opened,
          emoji_comment_picker_id,
          _record.title_opened,
          _record.focus_comment,
          _record.popup_more_comment_id,
          _record.elements,
          _record.improvements_conversations,
          _record.conversations_input,
        );
        let _pipe = _block;
        let _pipe$1 = ((_capture) => {
          return $state.set_proposals(state, _capture);
        })(_pipe);
        return $pair.new$(_pipe$1, $model.unsubscribe_more_proposal(state));
      }
    } else {
      let emoji_comment_picker_id = new Some(comment_id);
      let _block;
      let _record = state.proposals;
      _block = new $proposals.Proposals(
        _record.all,
        _record.filters,
        _record.filters_opened,
        _record.selected_questions,
        _record.add_opened,
        _record.more_question_opened,
        _record.more_proposal_opened,
        _record.more_unsubscriber,
        _record.block_page_size,
        _record.collaborators_opened,
        _record.change_owner_popup_opened,
        _record.change_status_popup_opened,
        _record.comment_displayed_opened,
        emoji_comment_picker_id,
        _record.title_opened,
        _record.focus_comment,
        _record.popup_more_comment_id,
        _record.elements,
        _record.improvements_conversations,
        _record.conversations_input,
      );
      let _pipe = _block;
      let _pipe$1 = ((_capture) => {
        return $state.set_proposals(state, _capture);
      })(_pipe);
      return $pair.new$(
        _pipe$1,
        (() => {
          let _pipe$2 = $dom.on_click(new $msg.UserClosedPopup("comment-emoji"));
          let _pipe$3 = $dom.to_cancel(
            _pipe$2,
            (var0) => { return new $msg.MenuReturnedUnsubscriber(var0); },
          );
          let _pipe$4 = $dom.once(_pipe$3);
          return $dom.subscribe(_pipe$4);
        })(),
      );
    }
  }
}
